import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface MediaValues {
  title: string;
  alt: string;
  tags: string[];
}

interface MediaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValues?: Partial<MediaValues>;
  onSubmit: (values: MediaValues) => void;
}

/** Edit an asset's metadata. Uploading/URLs happen at creation time. */
export function MediaDialog({ open, onOpenChange, initialValues, onSubmit }: MediaDialogProps) {
  const [title, setTitle] = useState(initialValues?.title ?? '');
  const [alt, setAlt] = useState(initialValues?.alt ?? '');
  const [tags, setTags] = useState((initialValues?.tags ?? []).join(', '));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit({
              title,
              alt,
              tags: tags
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean),
            });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit media</DialogTitle>
            <DialogDescription>Update this asset's details.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="media-title">Title</Label>
            <Input
              id="media-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Display title"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="media-alt">Alt text</Label>
            <Input
              id="media-alt"
              value={alt}
              onChange={(event) => setAlt(event.target.value)}
              placeholder="Describe the asset for accessibility"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="media-tags">Tags</Label>
            <Input
              id="media-tags"
              value={tags}
              onChange={(event) => setTags(event.target.value)}
              placeholder="Comma separated, e.g. hero, bloom"
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Save changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
